import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import {
    Backdrop,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    styled,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { StepperControls, StepWrapper, StyledCardSection } from '@fnol/library/src/components';
import { useApi } from '@fnol/library/src/hooks';
import { common, GenericObject, getApplicationId, getIsWebsite } from '@fnol/library/src/lib';
import { getClaimConfirmation, getClaimConfirmationAnonymous } from '../../rpc/actions';
import { riskTypeLife, submitterRole, transferTypeValues, translations } from '../../lib/consts';
import SuccessScreen from '../SuccessScreen';
import UserNotificationScreen from '../UserNotificationScreen';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

type Props = {
    navigator: GenericObject[];
};

let data = new FormData();

const ConfirmationScreen = ({ navigator }: Props) => {
    const { values, setErrors, setTouched, setFieldValue } = useFormikContext<any>();
    const [currentStep, { goForward, goBack }] = navigator;
    const [singleUserPaymentType, setSingleUserPaymentType] = useState('');

    useEffect(() => {
        if (values['outliveFullPayment'] === 'true') {
            setSingleUserPaymentType(translations.confirmationTable.FULL_PAYMENT);
        } else if (values['outliveFullPayment'] === 'false' && values['outliveTransfer'] === 'true') {
            if (values['transferType'] === transferTypeValues.full) {
                setSingleUserPaymentType(translations.confirmationTable.FULL_TRANSFER);
            } else if (values['transferType'] === transferTypeValues.partial) {
                setSingleUserPaymentType(translations.confirmationTable.PARTIAL_TRANSFER);
            }
        }
    }, [values['outliveFullPayment'], values['transferType']]);

    const hideStepper = () => {
        const allWithClass = Array.from(document.getElementsByClassName('MuiStepper-root'));

        allWithClass.forEach((element: Element) => {
            if (element instanceof HTMLElement) {
                element.style.display = 'none';
            }
        });
    };

    const { response: claimConfirmation, fetchMethod: fetchClaimConfirmation } = useApi(
        () => getClaimConfirmation(data),
        hideStepper,
    );

    const { response: claimConfirmationAnonymous, fetchMethod: fetchClaimConfirmationAnonymous } = useApi(
        () => getClaimConfirmationAnonymous(data),
        hideStepper,
    );

    const onCheckBoxChange = (e: any) => {
        setFieldValue('contactAgreement', e.target.checked);
    };

    const handleSubmit = (): void => {
        if (!claimConfirmation.isLoading && !claimConfirmationAnonymous.isLoading) {
            data = new FormData();
            data.append('stetaTempId', values.tempID);
            data.append('saglasnost', values.contactAgreement);

            if (getIsWebsite(getApplicationId())) {
                setTouched(
                    {
                        oneTimePass: true,
                    },
                    false,
                );

                const existingErrors: GenericObject = {};
                if (values.oneTimePass === '' || values.oneTimePass === null) {
                    existingErrors.oneTimePass = translations.validationMessages.REQUIRED_FIELD;
                    setErrors(existingErrors);
                } else {
                    data.append('kontakt', values.tempPassContact);
                    data.append('sifra', values.oneTimePass);
                    fetchClaimConfirmationAnonymous();
                }
            } else {
                fetchClaimConfirmation();
            }
        }
    };

    const handleGoBack = (): void => {
        goBack();
    };

    const toDate = (date: string) => {
        return new Date(date);
    };

    const updDate = `${('0' + toDate(values.dateOfOccurance).getDate()).slice(-2)}.${(
        '0' +
        (toDate(values.dateOfOccurance).getMonth() + 1)
    ).slice(-2)}.${toDate(values.dateOfOccurance).getFullYear()}`;

    const createData = (title: string, data: string) => {
        return { title, data };
    };

    const getPaymentType = (userId: number) => {
        let userPaymentType = '';
        if (values[`outliveFullPayment${userId}`] === 'true') {
            userPaymentType = translations.confirmationTable.FULL_PAYMENT;
        } else if (values[`outliveFullPayment${userId}`] === 'false' && values[`outliveTransfer${userId}`] === 'true') {
            if (values[`transferType${userId}`] === transferTypeValues.full) {
                userPaymentType = translations.confirmationTable.FULL_TRANSFER;
            } else if (values[`transferType${userId}`] === transferTypeValues.partial) {
                userPaymentType = translations.confirmationTable.PARTIAL_TRANSFER;
            }
        }
        return userPaymentType;
    };

    const getUsers = (userId: number) => {
        return values[`user${userId}Name`]
            ? `${values[`user${userId}Name`]} ${values[`user${userId}LastName`]} ${getPaymentType(userId)}`
            : '';
    };

    let users = '';
    const availableIds = [1, 2, 3, 4, 5];
    const userArray = availableIds.map((userId) => getUsers(userId));

    if (userArray.length > 0) {
        users = userArray.filter(Boolean).join(', ');
    }

    if (values.isSubmitterUser === 'false' && !!values.userName) {
        const user = `${values.userName} ${values.userLastName} - ${singleUserPaymentType}`;
        users = users !== '' ? user.concat(', ', users) : user;
    }

    if (values.isSubmitterUser === 'true') {
        const submitter = `${values.submitterName} ${values.submitterLastName} - ${singleUserPaymentType}`;
        users = !values.risks.includes(riskTypeLife.outlive)
            ? users !== ''
                ? submitter.concat(', ', users)
                : submitter
            : users;
    }

    const basicInfoRows = [
        createData(
            !values.risks.includes(riskTypeLife.outlive)
                ? translations.confirmationTable.DATE_OF_ACCIDENT
                : translations.confirmationTable.POLICY_VALID_TO,
            !values.risks.includes(riskTypeLife.outlive) ? updDate : values.policyValidTo,
        ),
        createData(
            !values.risks.includes(riskTypeLife.outlive) ? translations.confirmationTable.POLICY_INSURED : '',
            !values.risks.includes(riskTypeLife.outlive) ? `${values.insuredPerson}` : '',
        ),
        createData(
            !values.risks.includes(riskTypeLife.outlive)
                ? translations.confirmationTable.POLICY_USER
                : translations.confirmationTable.POLICY_USER_AND_PAYMENT,
            users,
        ),
        createData(
            translations.confirmationTable.POLICY_SUBMITTER,
            `${values.submitterName} ${values.submitterLastName}`,
        ),
        createData(
            translations.confirmationTable.POLICY_SUBMITTER_CONTACT,
            `${values.submitterPhone}, ${values.submitterEmail}`,
        ),
    ];

    const purchaseInfoRows = [
        createData(translations.confirmationTable.purchase.AMOUNT, `${values.purchaseAmount} ${values.currency}`),
        createData(translations.confirmationTable.purchase.PERIOD, values.purchaseValuePeriod),
        createData(translations.confirmationTable.purchase.DATE, updDate),
        createData(translations.confirmationTable.purchase.CONTRACTOR, `${values.userName} ${values.userLastName}`),
        createData(
            translations.confirmationTable.POLICY_SUBMITTER,
            values.submitterRole === submitterRole.contractor
                ? translations.confirmationTable.purchase.CONTRACTOR
                : `${values.submitterName} ${values.submitterLastName}`,
        ),
        createData(
            translations.confirmationTable.POLICY_SUBMITTER_CONTACT,
            values.submitterRole === submitterRole.contractor
                ? `${values.userPhone}, ${values.userEmail}`
                : `${values.submitterPhone}, ${values.submitterEmail}`,
        ),
        createData(translations.confirmationTable.purchase.PAYMENT_TYPE, `${singleUserPaymentType}`),
    ];

    const requestsListRows = values.riskNames?.map((riskName: string) => createData(riskName, riskName));

    return (
        <StepWrapper>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={claimConfirmation.isLoading || claimConfirmationAnonymous.isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <StyledCardSection>
                {!claimConfirmation.isSuccess && !claimConfirmationAnonymous.isSuccess ? (
                    <>
                        <TableContainer sx={{ paddingBottom: 7 }}>
                            <Table sx={{ minWidth: 230 }} aria-label="customized table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>
                                            {translations.confirmationScreen.POLICY_NUMBER}
                                        </StyledTableCell>
                                        <StyledTableCell align="left">{values.activePolicyNumber}</StyledTableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {!values.risks.includes(riskTypeLife.purchase)
                                        ? basicInfoRows.map(
                                              (row) =>
                                                  row.title && (
                                                      <StyledTableRow key={row.title}>
                                                          <StyledTableCell component="th" scope="row">
                                                              {row.title}
                                                          </StyledTableCell>
                                                          <StyledTableCell align="left">{row.data}</StyledTableCell>
                                                      </StyledTableRow>
                                                  ),
                                          )
                                        : purchaseInfoRows.map(
                                              (row) =>
                                                  row.title && (
                                                      <StyledTableRow key={row.title}>
                                                          <StyledTableCell component="th" scope="row">
                                                              {row.title}
                                                          </StyledTableCell>
                                                          <StyledTableCell align="left">{row.data}</StyledTableCell>
                                                      </StyledTableRow>
                                                  ),
                                          )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {!(
                            values.risks.includes(riskTypeLife.outlive) || values.risks.includes(riskTypeLife.purchase)
                        ) && (
                            <>
                                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                                    {translations.confirmationScreen.OPENED_REQUESTS}
                                </Typography>

                                <TableContainer sx={{ paddingBottom: 7 }}>
                                    <Table sx={{ minWidth: 230 }} aria-label="customized table">
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell>
                                                    {translations.confirmationScreen.REQUEST}
                                                </StyledTableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {requestsListRows.map((row: GenericObject) => (
                                                <StyledTableRow key={row.data}>
                                                    <StyledTableCell width={'35%'} component="th" scope="row">
                                                        {row.title}
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}

                        <Typography style={{ fontSize: '13px' }}>
                            {translations.confirmationScreen.PRIVACY_NOTE}
                        </Typography>

                        <FormGroup>
                            <FormControlLabel
                                name="contactAgreement"
                                control={<Checkbox />}
                                onChange={onCheckBoxChange}
                                label={
                                    <Typography style={{ fontSize: '13px' }}>
                                        {translations.confirmationScreen.AGREEMENT}
                                    </Typography>
                                }
                                componentsProps={{ typography: { fontSize: '13px' } }}
                            />
                        </FormGroup>

                        {getIsWebsite(getApplicationId()) && <UserNotificationScreen tempId={values.tempID} />}
                    </>
                ) : (
                    <SuccessScreen
                        claims={
                            claimConfirmation.data.data
                                ? claimConfirmation.data.data
                                : claimConfirmationAnonymous.data.data
                        }
                    />
                )}

                {claimConfirmationAnonymous.error.message && !claimConfirmationAnonymous.isSuccess && (
                    <Typography className="confirmation-message">
                        {claimConfirmationAnonymous.error.response?.data.match(/^\([0-9]+\)/)
                            ? translations.confirmationScreen.WRONG_PASS
                            : translations.confirmationScreen.CLAIM_ERROR}
                    </Typography>
                )}

                {claimConfirmation.error.message && !claimConfirmation.isSuccess && (
                    <Typography className="confirmation-message">
                        {claimConfirmation.error.response?.data.match(/^\([0-9]+\)/)
                            ? translations.confirmationScreen.WRONG_PASS
                            : translations.confirmationScreen.CLAIM_ERROR}
                    </Typography>
                )}
            </StyledCardSection>

            <>
                {!claimConfirmation.isSuccess && !claimConfirmationAnonymous.isSuccess ? (
                    <StepperControls onPrevious={handleGoBack} onNext={handleSubmit} onNextLabel={common.SEND} />
                ) : null}
            </>
        </StepWrapper>
    );
};

export default ConfirmationScreen;
